import { useEffect, useState } from 'react';
import { invokeAiFunction, kuunda, kuundaErrorMessage } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { downloadTextFile } from '@/lib/downloadText';
import { buildMarkdownPdfHtml, openPrintablePdf } from '@/lib/exportPdf';
import { CollapsibleCard } from '@/features/ui/CollapsibleCard';
import { useToast } from '@/features/ui/toast/useToast';
import './pilotage.css';

const TEMPLATES = [
  {
    id: 'kpi-strategiques',
    title: 'Tableau de bord KPI stratégiques',
    description: 'Suivi mensuel des indicateurs liés aux 6 piliers de la matrice.',
    filename: 'modele-kpi-strategiques.csv',
    content: `Pilier;Indicateur;Cible;Valeur actuelle;Écart;Période;Responsable;Commentaire
Orientation stratégique;Taux de réalisation des objectifs;100%;;;Mensuel;;
Offre de valeur;Nombre de produits lancés;;; ;Mensuel;;
Développement commercial;Nombre de clients acquis;;; ;Mensuel;;
Organisation et opérations;Taux de conformité qualité;98%;; ;Mensuel;;
Performance financière;Marge nette;15%;; ;Mensuel;;
Innovation et développement;Nombre d'innovations;;; ;Trimestriel;;`,
  },
  {
    id: 'suivi-objectifs',
    title: 'Suivi des objectifs SMART',
    description: 'Planifier et mesurer chaque objectif stratégique du projet.',
    filename: 'modele-suivi-objectifs-smart.csv',
    content: `Objectif SMART;Pilier;Date début;Date fin;Statut;Avancement %;Responsable;Preuves / livrables
;;;;À démarrer;0;;;`,
  },
  {
    id: 'actions-prioritaires',
    title: 'Plan d’actions prioritaires',
    description: 'Liste des actions issues de la matrice avec échéances et statut.',
    filename: 'modele-actions-prioritaires.csv',
    content: `Action;Pilier;Priorité;Échéance;Statut;Responsable;Résultat attendu
;;Haute;;À faire;;`,
  },
  {
    id: 'revue-strategique',
    title: 'Revue stratégique trimestrielle',
    description: 'Canevas de revue (faits marquants, écarts, décisions).',
    filename: 'modele-revue-strategique.md',
    content: `# Revue stratégique trimestrielle

## Période
- Trimestre :
- Projet :
- Date de revue :

## Faits marquants
-

## Avancement par pilier
| Pilier | Ce qui avance | Freins | Décision |
|--------|---------------|--------|----------|
| Orientation stratégique | | | |
| Offre de valeur | | | |
| Développement commercial | | | |
| Organisation et opérations | | | |
| Performance financière | | | |
| Innovation et développement | | | |

## Décisions et prochaines étapes
1.
`,
  },
] as const;

interface Props {
  projectId: string;
  projectTitle?: string;
}

export function StrategicDashboardPanel({ projectId, projectTitle }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    void kuunda
      .from('strategic_syntheses')
      .select('content')
      .eq('project_id', projectId)
      .maybeSingle()
      .then(({ data }) => setContent(data?.content ?? ''));
  }, [projectId]);

  async function generateSynthesis() {
    if (!profile || !projectId) return;
    setLoading(true);
    try {
      const result = await invokeAiFunction<{ content: string }>('ai-analyze', {
        kind: 'strategic_synthesis',
        project_id: projectId,
      });
      const next = result.content ?? '';
      setContent(next);
      const { error: err } = await kuunda.from('strategic_syntheses').upsert(
        { user_id: profile.id, project_id: projectId, content: next },
        { onConflict: 'project_id' },
      );
      if (err) throw err;
      toast.success('Synthèse stratégique générée.');
    } catch (e) {
      toast.error(kuundaErrorMessage(e, 'Synthèse impossible'));
    } finally {
      setLoading(false);
    }
  }

  function downloadSynthesis() {
    const title = projectTitle
      ? `Rapport de synthèse stratégique — ${projectTitle}`
      : 'Rapport de synthèse stratégique';
    openPrintablePdf(title, buildMarkdownPdfHtml(content || ''));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <CollapsibleCard
        title="Tableau de bord stratégique — modèles"
        badge={`${TEMPLATES.length}`}
        collapsedHint="Section repliée — dépliez pour télécharger les modèles KPI / SMART / actions."
      >
        <p className="muted u-synth-section-desc">
          Téléchargez librement des modèles d’outils de tableau de bord pour votre pilotage.
        </p>
        <div className="pilotage-templates">
          {TEMPLATES.map((t) => (
            <article key={t.id} className="pilotage-template-card">
              <h4>{t.title}</h4>
              <p>{t.description}</p>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => downloadTextFile(t.filename, t.content)}
              >
                Télécharger
              </button>
            </article>
          ))}
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Synthèse stratégique"
        badge={content ? 'Prête' : 'À générer'}
        defaultOpen={Boolean(content)}
        collapsedHint="Section repliée — dépliez pour générer ou consulter le rapport IA."
      >
        <div className="pilotage-section-head" style={{ marginTop: '0.65rem' }}>
          <p className="muted" style={{ margin: 0 }}>
            Rapport IA lié au projet sélectionné, à partir de la vision, du SWOT et de la matrice
            stratégique.
          </p>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-accent"
              disabled={loading || !projectId}
              onClick={() => void generateSynthesis()}
            >
              {loading ? 'Génération…' : 'Générer la synthèse stratégique'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={!content}
              onClick={downloadSynthesis}
            >
              PDF synthèse stratégique
            </button>
          </div>
        </div>
        {content ? (
          <pre className="pilotage-synthesis">{content}</pre>
        ) : (
          <p className="u-empty">Aucune synthèse pour ce projet. Cliquez sur générer.</p>
        )}
      </CollapsibleCard>
    </div>
  );
}
